// --------------------------------------------------------------
// Created On: 2021-09-01
// Author: Zachary Thomas
//
// Last Modified: 2025-11-19
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import styles from "./SvgPrintablePage.module.scss";

// This is a single printable page from an SVG. The page includes an image of the current subassembly, some basic info
// about the part, and a list of all the parts that make up the subassembly.
export default function SvgPrintablePage(props: Props): Component {
  // We can only show the print overlay in Chromium browsers.
  // @ts-expect-error - This is a browser check and TypeScript doesn't expect this browser info to exist in the window.
  const isChromiumBrowser = window.chrome;
  const [svgExists, setSvgExists] = useState<boolean>(true);
  const currentAssembly = useMemo(() => getAssemblyFromSvgPath(props.svgPath), [props.svgPath]);
  const currentDescription = useMemo(
    () => getDescriptionFromSvgPath(props.svgPath, props.topLevelBom, props.topLevelBomDescription, props.parts),
    [props.svgPath, props.topLevelBom, props.topLevelBomDescription, props.parts]
  );
  const currentWeight = useMemo(
    () => getWeightFromSvgPath(props.svgPath, props.topLevelBom, props.topLevelBomWeight),
    [props.svgPath, props.topLevelBom, props.topLevelBomWeight]
  );

  // Given an SVG path, returns the part assembly.
  function getAssemblyFromSvgPath(svgPath: string): string {
    return svgPath.split("/").slice(-1)[0].split(".")[0];
  }

  // Given an SVG path, returns the part description.
  function getDescriptionFromSvgPath(
    svgPath: string,
    topLevelBom: string,
    topLevelBomDescription: string,
    parts: Part[]
  ): string {
    let description = "";
    const assembly = getAssemblyFromSvgPath(svgPath);
    if (assembly === topLevelBom) {
      description = topLevelBomDescription;
    } else {
      const part = getPartByAssembly(assembly, parts);
      if (part !== null) {
        description = part.description;
      }
    }
    return description;
  }

  // Given an SVG path, returns the part weight.
  function getWeightFromSvgPath(svgPath: string, topLevelBom: string, topLevelBomWeight: number): number {
    let weight = 0.0;
    const assembly = getAssemblyFromSvgPath(svgPath);
    if (assembly.includes(topLevelBom)) {
      weight = topLevelBomWeight;
    }
    return weight;
  }

  // Given an SVG path, returns the child parts of the specific part.
  function getChildPartsFromSvgPath(svgPath: string, topLevelBom: string, parts: Part[]): Part[] {
    let childParts: Part[] = [];
    let assembly = getAssemblyFromSvgPath(svgPath);
    if (assembly.includes("X-Section")) {
      assembly = assembly.split(" ")[0];
    }
    if (assembly === topLevelBom) {
      childParts = parts;
    } else {
      const part = getPartByAssembly(assembly, parts);
      if (part !== null) {
        childParts = part.parts;
      }
    }
    return childParts;
  }

  // Recursively search for a part that has a part number that is a substring of the passed in assembly.
  function getPartByAssembly(assembly: string, parts: Part[]): Part | null {
    for (const part of parts) {
      if (assembly.includes(part.partNumber)) {
        return part;
      } else {
        const foundPart = getPartByAssembly(assembly, part.parts);
        if (foundPart !== null) {
          return foundPart;
        }
      }
    }
    return null;
  }

  return svgExists ? (
    <div className={isChromiumBrowser ? styles.chromiumBody : styles.body}>
      {/* Image of the current subassembly. */}
      <ReactSVG src={props.svgPath} className={styles.printableSvg} wrapper="div" onError={() => setSvgExists(false)} />

      {isChromiumBrowser && (
        <Fragment>
          {/* Disclaimer about the drawing. */}
          <div className={styles.disclaimer}>
            This drawing is uncontrolled. Contact your sales representative for up-to-date versions.
          </div>

          {/* List of all the parts that make up the subassembly. */}
          <div className={styles.partsTableContainer}>
            {getChildPartsFromSvgPath(props.svgPath, props.topLevelBom, props.parts).length > 0 && (
              <table className={`${styles.table} table table-bordered m-0 w-100`}>
                <thead className="thead-dark">
                  <tr>
                    <th>PART NUMBER</th>
                    <th>DESCRIPTION</th>
                    <th>QUANTITY</th>
                  </tr>
                </thead>
                <tbody>
                  {getChildPartsFromSvgPath(props.svgPath, props.topLevelBom, props.parts).map((part) => (
                    <tr key={part.partNumber}>
                      <td>
                        <b>{part.partNumber}</b>
                      </td>
                      <td>{part.description}</td>
                      <td>
                        {part.buildQuantity}&nbsp;{part.units}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Basic info about the part. */}
          <div className={`${styles.partInfo} container`}>
            <div className="row">
              <div className={`${styles.partInfoBorder} col-6`}>
                <b>Top Level Bom:</b> {props.topLevelBom}
              </div>
              {props.topLevelBom !== currentAssembly && (
                <div className={`${styles.partInfoBorder} col-6`}>
                  <b>Assembly:</b> {currentAssembly}
                </div>
              )}
            </div>
            <div className="row">
              <div className={`${styles.partInfoBorder} col-2`}>
                <img className={styles.logo} src="/logo.png" alt="Cornell Pump Company Logo" />
              </div>
              <div className="col-10">
                <div className="row">
                  <div className={`${styles.partInfoBorder} col`}>
                    <b>Description:</b> {currentDescription}
                  </div>
                </div>
                <div className="row">
                  <div className={`${styles.partInfoBorder} font-weight-bold col`}>
                    Cornell Pump Company, Portland Oregon
                  </div>
                  {currentAssembly.includes(props.topLevelBom) && (
                    <div className={`${styles.partInfoBorder} col`}>
                      <b>Approx. Weight:</b> {currentWeight > 0 ? `${currentWeight} LB` : "Unknown"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  ) : (
    // Do not render the page if the SVG cannot be rendered.
    <Fragment></Fragment>
  );
}

interface Props {
  svgPath: string;
  topLevelBom: string;
  topLevelBomDescription: string;
  topLevelBomWeight: number;
  parts: Part[];
}

interface Part {
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  weight: number;
  isSellable: boolean;
  hasOtherMaterial: boolean;
  isImpeller: boolean;
  parts: Part[];
}
